import ky, { HTTPError, type BeforeErrorHook, type InitHook } from "ky";
import {
  AUTH_API_URL,
  userManager,
  getLogisticsApiUrl,
  getPatientApiUrl,
  getNotificationApiUrl,
} from "../utils/envUtils";
import { User } from "oidc-client-ts";

const signinRedirect = () => {
  return userManager.signinRedirect({
    state: window.location.pathname,
    acr_values: "mfa",
  });
};

const kyBase = ky.create({
  retry: { limit: 0 }, // axios-like
  timeout: false, // axios-like
});

const handleAuthError: BeforeErrorHook = ({ error }) => {
  // Network failures, timeouts, etc. are NetworkErrors with no `.response`;
  // only HTTP errors carry a status to act on.
  if (error instanceof HTTPError) {
    // Re-authenticate when the token is expired/invalidated (401) or its MFA
    // timestamp is too old (403).
    if (error.response.status === 401 || error.response.status === 403) {
      void signinRedirect();
    }
  }
  return error;
};

// Resolve the organization-specific base URL and attach auth in an `init` hook (not
// `beforeRequest`). `init` mutates the options before ky builds the Request, so
// we set `prefix` instead of reconstructing the Request via `new Request(url,
// request)` — the latter drops the body of POST/PATCH/PUT requests in Firefox
// (the body is a ReadableStream that doesn't survive reconstruction).
const authedInitHook =
  (buildBaseUrl: (organization: string) => string): InitHook =>
  (options) => {
    // Test: no session, no token; resolve against the "medoma" organization.
    if (import.meta.env.MODE === "test") {
      options.prefix = buildBaseUrl("medoma");
      return;
    }

    // getAuthenticatedUser() throws + redirects if there's no live session,
    // so we never build a tokenless request in prod.
    const user = getAuthenticatedUser();
    // The auth service names the organization claim `unit`.
    options.prefix = buildBaseUrl(user.profile.unit as string);

    const headers = new Headers(options.headers as HeadersInit | undefined);
    headers.set("Authorization", `Bearer ${user.access_token}`);
    options.headers = headers;
  };

export const logisticsApi = kyBase.extend({
  hooks: {
    init: [authedInitHook(getLogisticsApiUrl)],
    beforeError: [handleAuthError],
  },
});

export const patientApi = ky.create({
  hooks: {
    init: [authedInitHook(getPatientApiUrl)],
    beforeError: [handleAuthError],
  },
});

export const notificationApi = ky.create({
  hooks: {
    init: [authedInitHook(getNotificationApiUrl)],
    beforeError: [handleAuthError],
  },
});

const getAuthenticatedUser = () => {
  const oidcStorage = sessionStorage.getItem(
    `oidc.user:${AUTH_API_URL}:medoma-care-reference`,
  );
  if (!oidcStorage) {
    void signinRedirect();
    throw new Error(
      "Missing session storage entry. Redirecting to signin to amend this.",
    );
  }

  const user = User.fromStorageString(oidcStorage);
  if (user.expired) {
    void signinRedirect();
    throw new Error(
      "The user's credentials have expired. Redirecting to signin to amend this.",
    );
  }

  return user;
};
